import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCheckCircle,
  FaChartLine,
  FaClipboardList,
  FaEdit,
  FaSpinner,
  FaMapMarkerAlt,
  FaBuilding,
  FaHome,
  FaRupeeSign,
} from "react-icons/fa";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

export default function ReportSchemes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { district } = location?.state;
  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStats, setFilteredStats] = useState({});

  console.log(district);

  const fetchSchemes = async () => {
    try {
      setLoading(true);
      const params = {
        pageNo: 1,
        pageSize: 10,
        district: district,
      };

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(data?.data || []);
      setFilteredStats(data?.filteredStats);
      setSchemeStats({
        totalSchemes: data?.totalSchemes || 0,
        totalDraftSchemes: data?.totalDraftSchemes || 0,
        totalCompletedSchemes: data?.totalCompletedSchemes || 0,
        totalAllocationAmount: data?.totalAllocationAmount || 0,
      });
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
    }
  };

  console.log(filteredStats);

  useEffect(() => {
    fetchSchemes();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "Complete":
        return "text-green-600 bg-green-50 border-green-200";
      case "In Progress":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "Draft":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Complete":
        return <FaCheckCircle className="inline mr-2" />;
      case "In Progress":
        return <FaSpinner className="inline mr-2 animate-spin" />;
      case "Draft":
        return <FaEdit className="inline mr-2" />;
      default:
        return <FaClipboardList className="inline mr-2" />;
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* -------------------- HEADER SECTION -------------------- */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold capitalize text-gray-800 flex items-center gap-3">
              <FaChartLine className="text-blue-600" />
              <div className="capitalize">{district}</div>
            </h1>
            <p className="text-gray-600 mt-2">
              Monitor and analyze all government proposals in real-time
            </p>
          </div>

          <button
            onClick={fetchSchemes}
            className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            <FaChartLine />
            Refresh Data
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg p-5 shadow border border-blue-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-lg">📊</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Total Proposals
                </p>
                <p className="text-xl font-bold text-gray-900">
                  {filteredStats.filteredTotalSchemes}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-green-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-green-600 text-lg">✅</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-xl font-bold text-green-600">
                  {filteredStats?.filteredTotalCompletedSchemes}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-yellow-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <span className="text-yellow-600 text-lg">⚡</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Draft</p>
                <p className="text-xl font-bold text-yellow-600">
                  {filteredStats?.filteredTotalDraftSchemes}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-5 shadow border border-purple-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <span className="text-purple-600 text-lg">💰</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">
                  Total Allocation
                </p>
                <p className="text-xl font-bold text-gray-900">
                  ₹
                  {filteredStats?.filteredTotalAllocationAmount?.toLocaleString(
                    "en-In"
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FaClipboardList className="text-blue-600" />
              Proposals Overview
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Showing {schemes?.length} of {schemes?.length} proposals
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <FaSpinner className="text-4xl text-blue-600 animate-spin" />
            </div>
          ) : schemes?.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No proposals found
              </h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Scheme Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Budget
                    </th>
                    {/* <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Progress
                    </th> */}
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <AnimatePresence>
                    {schemes?.map((item, index) => (
                      <motion.tr
                        key={item.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className="hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                        onClick={() =>
                          navigate("../schemeDetails", {
                            state: { schemeId: item?.id },
                          })
                        }
                      >
                        <td className="px-6 py-4">
                          <div>
                            <h4 className="font-semibold text-gray-800 text-lg">
                              {item.schemeName}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              ID: {item.schemeCode || "N/A"}
                            </p>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-gray-700">
                              <FaMapMarkerAlt className="text-blue-500" />
                              <span>{item.district}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <FaBuilding className="text-green-500" />
                              <span>{item.block}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <FaHome className="text-purple-500" />
                              <span>{item.panchayat}</span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-lg font-bold text-gray-800">
                            <FaRupeeSign className="text-green-600" />
                            {(item.allocatedAmount / 100000).toFixed(2)} L
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            Allocated Budget
                          </p>
                        </td>

                        {/* <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{
                                    width: `${item.progressPercentage}%`,
                                  }}
                                  transition={{ duration: 1, delay: 0.3 }}
                                  className={`h-full rounded-full ${
                                    item.progressPercentage >= 70
                                      ? "bg-green-500"
                                      : item.progressPercentage >= 30
                                      ? "bg-blue-500"
                                      : "bg-yellow-500"
                                  }`}
                                />
                              </div>
                            </div>
                            <div className="flex items-center gap-1 text-gray-700 font-semibold">
                              <FaPercent className="text-sm" />
                              {item.progressPercentage}%
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            Completion
                          </p>
                        </td> */}

                        <td className="px-6 py-4">
                          <div
                            className={`inline-flex items-center px-4 py-2 rounded-full border ${getStatusColor(
                              item.schemeStatus
                            )}`}
                          >
                            {getStatusIcon(item.schemeStatus)}
                            <span className="font-semibold">
                              {item.schemeStatus}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {/* Last updated: {item.lastUpdated || "N/A"} */}
                          </p>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
